import React from 'react';
import { motion } from 'framer-motion';
import { Lock, Crown, ArrowRight, LogOut, Sun, Moon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const RestrictedAccess = ({ isDarkMode, setIsDarkMode }) => {
    const navigate = useNavigate();
    const { signOut } = useAuth();

    return (
        <div className={`min-h-screen flex items-center justify-center p-4 relative transition-colors duration-500 ${isDarkMode ? 'bg-[#050505]' : 'bg-[#FCFDFE]'
            }`}>
            {/* Background Orbs */}
            {isDarkMode ? (
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-600/5 rounded-full blur-[100px] pointer-events-none" />
            ) : (
                <>
                    <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-blue-400/5 rounded-full blur-[100px] pointer-events-none" />
                    <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] bg-orange-500/5 rounded-full blur-[100px] pointer-events-none" />
                </>
            )}

            {/* Theme Toggle in Restricted Access */}
            <div className="absolute top-8 right-8 z-20">
                <button
                    onClick={() => setIsDarkMode(!isDarkMode)}
                    className={`p-2.5 rounded-xl transition-all shadow-sm ${isDarkMode ? 'bg-white/5 hover:bg-white/10 text-yellow-400' : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
                        }`}
                >
                    {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                </button>
            </div>

            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className={`max-w-md w-full border rounded-[48px] p-10 text-center relative z-10 transition-colors ${isDarkMode
                        ? 'bg-[#111111]/80 backdrop-blur-md border-white/[0.08] shadow-[0_20px_50px_rgba(0,0,0,0.5)]'
                        : 'bg-white border-slate-200 shadow-2xl shadow-slate-200/50'
                    }`}
            >
                <div className={`w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-inner ${isDarkMode ? 'bg-white/5' : 'bg-orange-50'
                    }`}>
                    <Lock className="text-orange-500 w-10 h-10" />
                </div>

                <h2 className={`text-3xl font-black mb-4 uppercase tracking-tighter ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    Access Restricted
                </h2>
                <p className={`font-medium mb-10 leading-relaxed ${isDarkMode ? 'text-gray-400' : 'text-slate-500'}`}>
                    Your 2-month free tier has expired. To continue exploring the cooling and heating rhythms of Swara Shastra, please upgrade to a Premium plan.
                </p>

                <div className="space-y-4">
                    <button
                        onClick={() => navigate('/pricing')}
                        className="w-full bg-gradient-to-r from-blue-600 to-orange-500 hover:scale-[1.02] text-white font-black py-5 rounded-[24px] shadow-xl shadow-orange-500/20 flex items-center justify-center space-x-3 transition-all"
                    >
                        <Crown className="w-5 h-5" />
                        <span>Upgrade to Premium</span>
                        <ArrowRight className="w-5 h-5" />
                    </button>

                    <button
                        onClick={signOut}
                        className={`w-full font-bold py-5 rounded-[24px] flex items-center justify-center space-x-3 transition-all border ${isDarkMode
                                ? 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10'
                                : 'bg-slate-50 border-slate-100 text-slate-400 hover:bg-slate-100'
                            }`}
                    >
                        <LogOut className="w-5 h-5" />
                        <span>Sign Out</span>
                    </button>
                </div>

                <p className={`mt-8 text-[10px] font-black uppercase tracking-[0.2em] ${isDarkMode ? 'text-gray-600' : 'text-slate-400'}`}>
                    Regional pricing starting at just ₹1/month
                </p>
            </motion.div>
        </div>
    );
};

export default RestrictedAccess;
